import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn, AISmartButtonOptions } from '../../types/types';
import type { EditorPluginAIConfigItemWithOptions } from '../../ui/prebuilt/config-items/config-items';

import { aiSmartButtonAPIKey } from './ai-smart-button-api-plugin-key';
import { createInitialState, createPluginState } from './plugin-factory';
import {
	chooseActionsToShowForSelection,
	getActionsListForSelection,
} from './utils/map-config-items-to-actions';
// eslint-disable-next-line import/order
import type { AISmartButtonAPIState } from './states';

export const createAISmartButtonAPIPlugin = ({
	aiGlobalOptIn,
	aiSmartButtonOptions,
	dispatch,
	dynamicSurfaceConfigItems,
	getIntl,
	// Optional options
	api,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	aiSmartButtonOptions: AISmartButtonOptions;
	// Optional options
	api?: ExtractInjectionAPI<AIPlugin>;
	dispatch: PMPluginFactoryParams['dispatch'];
	dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[];
	getIntl: PMPluginFactoryParams['getIntl'];
	getMentionNameDetails?: GetMentionNameDetails;
	product: string;
}): SafePlugin<AISmartButtonAPIState> => {
	const intl = getIntl();

	const { removeActions, removeContext, setActions, setContext } = aiSmartButtonOptions.actions;

	function isInEditMode() {
		const editorViewModeState = api?.editorViewMode?.sharedState.currentState();
		const mode = editorViewModeState?.mode;
		const isInEditMode = !!(mode && mode === 'edit');
		return isInEditMode;
	}

	return new SafePlugin({
		state: createPluginState(dispatch, createInitialState()),
		key: aiSmartButtonAPIKey,
		view: (editorView) => {
			const { emptyDocActions, nonEmptyDocActions, selectionActions } = getActionsListForSelection({
				aiGlobalOptIn,
				dynamicSurfaceConfigItems,
				intl,
				view: editorView,
			});

			// Track the edit mode state
			let wasInEditMode = isInEditMode();

			// Track the doc state and selection state to avoid resetting actions unnecessarily
			let wasDocumentEmpty = isEmptyDocument(editorView.state.doc);
			let wasSelectionEmpty = editorView.state.selection.empty;
			let wasCleanedUp = false;

			// Register editor actions only in edit page or edit mode of live page.
			if (wasInEditMode) {
				setActions(
					chooseActionsToShowForSelection(
						{
							isEmptyDoc: isEmptyDocument(editorView.state.doc),
							isEmptySelection: editorView.state.selection.empty,
						},
						{
							emptyDocActions,
							nonEmptyDocActions,
							selectionActions,
						},
					),
				);
			}

			const reference = intl.formatMessage(aiMessages.aiSmartButtonDynamicSurfaceSelectedContent);
			/**
			 * On removing reference, focus the editor.
			 */
			const onRemoveReference = () => {
				editorView.focus();
			};

			function cleanup() {
				removeActions();
				removeContext();
				wasCleanedUp = true;
			}

			return {
				update: (editorView, prevState) => {
					const currentEditorState = editorView.state;
					const currentlyInEditMode = isInEditMode();
					const switchedFromViewToEdit = !wasInEditMode && currentlyInEditMode;

					/**
					 * Switching from edit to view mode, we need to remove
					 * 	all actions, context and nudges.
					 */
					if (wasInEditMode && !currentlyInEditMode) {
						cleanup();
					}
					wasInEditMode = currentlyInEditMode;

					if (!currentlyInEditMode) {
						return;
					}

					/**
					 * Switching from view mode to edit mode, editor state doesn't change.
					 * But editing actions must be added again as it had been removed
					 * 	when transitioning to edito -> view mode.
					 */
					if (
						currentEditorState.doc.eq(prevState.doc) &&
						currentEditorState.selection.empty === prevState.selection.empty &&
						!switchedFromViewToEdit
					) {
						return;
					}

					/**
					 * Update;
					 * - registry of editor actions
					 * - context on selection change
					 * - nudges related stuff
					 * only in edit page or edit mode of live page.
					 */
					const isCurrentSelectionEmpty = currentEditorState.selection.empty;
					const isCurrentDocEmpty = isEmptyDocument(currentEditorState.doc);

					// only reset actions if the selection or document state has changed
					// this avoids unnecessary re-renders for editor
					if (
						wasCleanedUp ||
						isCurrentSelectionEmpty !== wasSelectionEmpty ||
						isCurrentDocEmpty !== wasDocumentEmpty
					) {
						setActions(
							chooseActionsToShowForSelection(
								{ isEmptyDoc: isCurrentDocEmpty, isEmptySelection: isCurrentSelectionEmpty },
								{
									emptyDocActions,
									nonEmptyDocActions,
									selectionActions,
								},
							),
						);

						wasSelectionEmpty = isCurrentSelectionEmpty;
						wasDocumentEmpty = isCurrentDocEmpty;
						wasCleanedUp = false;
					}

					/**
					 * Check for dynamic surface context change on every selection change.
					 * Not using onSelectionChanged in pluginFactory because
					 * it will be called only when selection changes by current user.
					 */
					if (!currentEditorState.selection.empty) {
						setContext({
							reference,
							onRemove: onRemoveReference,
							sourceId: 'editor',
						});
					} else {
						removeContext();
					}
				},
				destroy: () => {
					cleanup();
				},
			};
		},
	});
};
